import { z } from 'zod';

/**
 * Assets, Team, Profile and the admin Partners list.
 *
 * Grouped in one file because they share a single rule: **this database owns almost
 * none of it.** WorkOS owns users and memberships, S3 owns the asset bytes, and the
 * partner's commercial terms are the only part that is genuinely ours. Each schema
 * below says which system is the source of truth, because that decides what a write
 * would have to do later.
 */

// ---------------------------------------------------------------- assets

export const assetFileSchema = z.object({
  id: z.string(),
  name: z.string(),
  /** Lowercase extension without the dot — the icon and the type column both use it. */
  kind: z.string(),
  sizeBytes: z.number().int(),
  updatedAt: z.string(),
});
export type AssetFile = z.infer<typeof assetFileSchema>;

/**
 * A partner a private folder has been granted to.
 *
 * Named as well as identified, because the id is meaningless on screen and having the
 * view resolve it would mean holding a partner list to look ids up in.
 */
export const folderGranteeSchema = z.object({
  partnerId: z.string(),
  name: z.string(),
});
export type FolderGrantee = z.infer<typeof folderGranteeSchema>;

export const assetFolderSchema = z.object({
  id: z.string(),
  name: z.string(),
  /**
   * Public folders are the shared brand library every partner sees. A private folder
   * belongs to one partner and must never appear in another's list.
   */
  isPublic: z.boolean(),
  fileCount: z.number().int(),
  files: z.array(assetFileSchema),
  /**
   * Who a private folder reaches. **Always empty for a partner viewer**, whatever the
   * grants say: which other companies share a folder is not their business, and the
   * server decides that rather than the view remembering to hide it.
   */
  grantedTo: z.array(folderGranteeSchema),
});
export type AssetFolder = z.infer<typeof assetFolderSchema>;

export const assetsResponseSchema = z.object({
  folders: z.array(assetFolderSchema),
  totalFiles: z.number().int(),
  /** True for Sparkery staff, who see private folders across all partners. */
  showsAllPartners: z.boolean(),
  /**
   * Whether this viewer may upload, create folders, delete and grant. Mirrors
   * `assets:manage`, so a read-only viewer is never offered a control that would 403.
   */
  canManage: z.boolean(),
  /**
   * Whether a bucket is configured at all.
   *
   * The portal runs without one — every asset screen is simply read-only — and the
   * view says so plainly rather than offering an upload that cannot work. False is a
   * deployment fact, not a permission.
   */
  storageConfigured: z.boolean(),
  /** The upload ceiling, so the page can reject a file before asking for a ticket. */
  maxUploadBytes: z.number().int().positive(),
  /** Every partner, for the grant picker. Empty unless the viewer may manage. */
  grantablePartners: z.array(folderGranteeSchema),
});
export type AssetsResponse = z.infer<typeof assetsResponseSchema>;

// ---------------------------------------------------------------- team

export const MEMBER_TYPES = ['admin', 'standard'] as const;
export const memberTypeSchema = z.enum(MEMBER_TYPES);
export type MemberType = z.infer<typeof memberTypeSchema>;

export const PARTNER_INVITATION_ROLES = ['partner_user', 'partner_admin'] as const;
export const partnerInvitationRoleSchema = z.enum(PARTNER_INVITATION_ROLES);
export type PartnerInvitationRole = z.infer<typeof partnerInvitationRoleSchema>;

export const teamMemberSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  type: memberTypeSchema,
  /** Two-letter monogram, computed once server-side so every surface agrees. */
  initials: z.string(),
  /** True for the viewer's own row — the UI must not offer to remove it. */
  isSelf: z.boolean(),
});
export type TeamMember = z.infer<typeof teamMemberSchema>;

export const teamResponseSchema = z.object({
  partnerId: z.string(),
  partnerName: z.string(),
  members: z.array(teamMemberSchema),
  /** Whether this viewer may invite and remove. Mirrors the `team:manage` permission. */
  canManage: z.boolean(),
});
export type TeamResponse = z.infer<typeof teamResponseSchema>;

export const invitePartnerMemberInputSchema = z.object({
  email: z.string().trim().email().max(320),
  role: partnerInvitationRoleSchema,
});
export type InvitePartnerMemberInput = z.infer<typeof invitePartnerMemberInputSchema>;

export const invitePartnerMemberResultSchema = z.object({
  invitationId: z.string(),
  email: z.string(),
  role: partnerInvitationRoleSchema,
  partnerId: z.string(),
  expiresAt: z.string().nullable(),
});
export type InvitePartnerMemberResult = z.infer<typeof invitePartnerMemberResultSchema>;

// ---------------------------------------------------------------- profile

export const profileResponseSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  initials: z.string(),
  /** Null for Sparkery staff, who belong to no partner. */
  partnerName: z.string().nullable(),
  partnerReference: z.string().nullable(),
  isCompanyAdmin: z.boolean(),
  /**
   * Whether the identity provider lets the portal change these.
   *
   * Automated test auth cannot, and an impersonated session must not. Rather than the
   * view guessing from the audience, the server states it.
   */
  editable: z.boolean(),
  /** Why not, when `editable` is false. Shown verbatim, so it must read as a sentence. */
  readOnlyReason: z.string().nullable(),
});
export type ProfileResponse = z.infer<typeof profileResponseSchema>;

// ---------------------------------------------------------------- partners (admin)

export const partnerRowSchema = z.object({
  partnerId: z.string(),
  name: z.string(),
  /**
   * The canonical `utm_source`, derived from the name at creation.
   *
   * Shown because it is not decoration: it is baked into every referral link the
   * partner has published and into every GA4 row already recorded, which is why
   * renaming a partner never changes it.
   */
  slug: z.string(),
  adminEmail: z.string(),
  createdAt: z.string(),
  initials: z.string(),
  commissionAccrues: z.boolean(),
  commissionRate: z.string().nullable(),
  linkCount: z.number().int(),
  codeCount: z.number().int(),

  /**
   * The WorkOS organisation this partner's users sign in through, or null.
   *
   * **This is the seat key.** A wrong value scopes a real person to another company's
   * revenue, so it is surfaced rather than hidden, and every change to it is audited.
   * Null means nobody from that company can sign in yet.
   */
  workosOrgId: z.string().nullable(),
  archived: z.boolean(),
  /** When, for the "Archived 4 Sep" line. Null while active. */
  archivedAt: z.string().nullable(),
});
export type PartnerRow = z.infer<typeof partnerRowSchema>;

export const partnersResponseSchema = z.object({
  partners: z.array(partnerRowSchema),
});
export type PartnersResponse = z.infer<typeof partnersResponseSchema>;

export const workosOrganizationOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  linkedPartnerId: z.string().nullable(),
  linkedPartnerName: z.string().nullable(),
});
export type WorkosOrganizationOption = z.infer<typeof workosOrganizationOptionSchema>;

export const workosOrganizationsResponseSchema = z.object({
  organizations: z.array(workosOrganizationOptionSchema),
});
export type WorkosOrganizationsResponse = z.infer<typeof workosOrganizationsResponseSchema>;

// ------------------------------------------------------- partner administration

/** A percentage rate: two decimal places, and never zero on a partner who accrues. */
const rateSchema = z
  .string()
  .regex(/^\d{1,2}(\.\d{1,2})?$/, 'a percentage like 12 or 12.5')
  .refine((value) => Number(value) > 0, 'a rate of zero is a fixed fee, not a percentage')
  .refine((value) => Number(value) <= 99.99, 'a rate above 99.99% is not a commission');

export const createPartnerInputSchema = z
  .object({
    name: z.string().trim().min(1).max(191),
    adminEmail: z.string().trim().email().max(320),
    /**
     * Set once, here, and never again.
     *
     * A fixed-fee partner must never start accruing retroactively, and a percentage
     * partner must never lose the ledger their statements are built from — so this is
     * the only place it appears in the API. Changing it means a new partner record.
     */
    commissionAccrues: z.boolean(),
    /** Required when `commissionAccrues`, refused when not. */
    commissionRate: rateSchema.optional(),
    /**
     * Optional at creation: omitted means the server should create a WorkOS
     * organisation for this partner when WorkOS management is configured.
     */
    workosOrgId: z.string().trim().min(1).max(64).optional(),
  })
  .refine((input) => input.commissionAccrues === (input.commissionRate !== undefined), {
    message: 'a commission partner needs a rate, and a fixed-fee partner must not have one',
    path: ['commissionRate'],
  });
export type CreatePartnerInput = z.infer<typeof createPartnerInputSchema>;

/**
 * What an admin may change afterwards.
 *
 * Note what is absent: `commissionAccrues` and `slug`. The first would rewrite what a
 * partner is owed; the second is published in links and recorded in GA4 history, so a
 * rename deliberately leaves it alone.
 */
export const updatePartnerInputSchema = z
  .object({
    name: z.string().trim().min(1).max(191).optional(),
    adminEmail: z.string().trim().email().max(320).optional(),
    /**
     * Only meaningful on a partner who accrues; refused on a fixed-fee one.
     *
     * It reaches the orders the next scrape accrues, not the ones already in the
     * ledger — every accrual freezes the rate it was computed at.
     */
    commissionRate: rateSchema.optional(),
    /** Null clears the mapping, which removes every seat at that company. */
    workosOrgId: z.string().trim().min(1).max(64).nullable().optional(),
  })
  .refine((input) => Object.values(input).some((value) => value !== undefined), {
    message: 'nothing to change',
  });
export type UpdatePartnerInput = z.infer<typeof updatePartnerInputSchema>;

export const partnerWriteResultSchema = z.object({
  partner: partnerRowSchema,
  /**
   * What actually changed, for the confirmation line.
   *
   * Field names as the admin knows them, so the view can say "rate and admin email
   * updated" rather than restating the whole record back at them.
   */
  changed: z.array(z.string()),
});
export type PartnerWriteResult = z.infer<typeof partnerWriteResultSchema>;

export const archivePartnerResultSchema = z.object({
  partner: partnerRowSchema,
  /**
   * Unsettled commission left behind at the moment of archiving.
   *
   * Reported rather than blocked: the money was earned before the relationship ended
   * and still settles into a final statement. Saying so is what stops it being a
   * surprise a month later.
   */
  unsettledCommission: z.string(),
  /** True when a WorkOS mapping was cleared, i.e. seats were actually revoked. */
  accessRevoked: z.boolean(),
});
export type ArchivePartnerResult = z.infer<typeof archivePartnerResultSchema>;

/**
 * A two-letter monogram from a display name.
 *
 * One word gives its first two letters, so "Bodyline" reads "BO" rather than a lonely
 * "B". Non-letters are dropped first, so "Halo Health Clinics (UK)" is still "HH".
 */
export function initialsOf(name: string): string {
  const words = name
    .split(/\s+/)
    .map((word) => word.replace(/[^\p{L}]/gu, ''))
    .filter(Boolean);

  if (words.length === 0) return '??';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}
